'use client'

import { useEffect } from 'react'
import { useOptimizedCartStore, useCartSync, CartProduct } from '@/stores/optimized-cart-store'
import { CartOperationFeedback } from './CartOperationFeedback'

interface CartProviderProps {
  children: React.ReactNode
  initialProducts?: unknown[]
  initialCartItems?: unknown[]
}

/**
 * CartProvider component that initializes the cart store and provides
 * optimistic updates with visual feedback for all cart operations
 */
function CartProvider({
  children,
  initialProducts = [],
  initialCartItems = []
}: CartProviderProps) {
  // Initialize cart sync for cross-tab synchronization
  useCartSync()

  // Initialize the cart store with server data
  useEffect(() => {
    const store = useOptimizedCartStore.getState()

    // Set initial products if provided
    if (initialProducts.length > 0) {
      store.setInitialProducts(initialProducts as CartProduct[])
    }

    // Initialize cart items if provided
    if (initialCartItems.length > 0) {
      // Transform server format to client format and add to cart
      initialCartItems.forEach((item: unknown) => {
        const cartItem = item as Record<string, unknown>
        store.addItem(
          (cartItem.product_id || cartItem.productId) as string,
          cartItem.quantity as number,
          (cartItem.selected_color || cartItem.selectedColor) as string | undefined,
          (cartItem.sku_id || cartItem.skuId) as string | undefined
        )
      })
    }

    // Sync cart state after initialization
    store.syncCart()
  }, [initialProducts, initialCartItems])

  return (
    <>
      {children}
      {/* Global cart operation feedback */}
      <CartOperationFeedback />
    </>
  )
}

export { CartProvider }
export default CartProvider